import React, {
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import GDPRContext from "/src/contexts/GDPRContext";
import TrackingTags from "../../tracking-tags/TrackingTags";
import {IS_PROD} from "/src/config";
import {isEUCountryCode} from "../utils/GDPRWrapperUtils";
import {isEMEACountryCode} from "/src/utils/geolocate/geolocateUtils";

interface GDPRWrapperProps {
  children?: ReactNode;
}

interface KetchGeoIP {
  countryCode: string;
  region: string;
}

const GDPRWrapper = (props: GDPRWrapperProps): ReactElement => {
  const {children} = props;
  const [geoData, setGeoData] = useState<{
    countryCode: string | null;
    isEU: boolean;
    isEMEA: boolean;
  }>({
    countryCode: null,
    isEU: false,
    isEMEA: false,
  });

  const handleGeoIP = useCallback((geoip: KetchGeoIP) => {
    if (!geoip || !geoip.countryCode) return;

    setGeoData(currentData => {
      if (currentData.countryCode === geoip.countryCode) {
        return currentData;
      }
      const {countryCode} = geoip;
      return {
        countryCode,
        isEU: isEUCountryCode(countryCode),
        isEMEA: isEMEACountryCode(countryCode),
      };
    });
  }, []);

  useEffect(() => {
    const initializeKetchInteraction = () => {
      if (window.ketch) {
        window.ketch("getGeoIP", handleGeoIP);
        window.ketch("on", "geoip", handleGeoIP);
      }
    };

    // Poll for the official `loaded` flag on the semaphore object
    const intervalId = setInterval(() => {
      if (window.semaphore?.loaded) {
        clearInterval(intervalId);
        initializeKetchInteraction();
      }
    }, 200);

    return () => {
      clearInterval(intervalId);
      if (window.ketch) {
        window.ketch("off", "geoip", handleGeoIP);
      }
    };
  }, [handleGeoIP]);

  const GDPRState = useMemo(() => ({...geoData}), [geoData]);

  return (
    <GDPRContext.Provider value={GDPRState}>
      {IS_PROD && <TrackingTags />}
      {children}
    </GDPRContext.Provider>
  );
};

export default GDPRWrapper;
